import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface MultiSelectAutocompleteProps<T extends object> {
  options: T[];
  value: T[];
  onChange: (event: React.SyntheticEvent, value: T[]) => void;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  label: string;
  disabled?: boolean;
  totalOptionsCount: number; // Used to determine if "All" should be displayed
}

export const MultiSelectAutocomplete = <T extends object>({
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  label,
  disabled = false,
  totalOptionsCount,
}: MultiSelectAutocompleteProps<T>) => {
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      fullWidth
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      renderInput={(params) => <TextField {...params} label={label} />}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
          {getOptionLabel(option)}
        </li>
      )}
      renderTags={(value) => {
        const numSelected = value.length;
        const total = totalOptionsCount;

        const textWrapper = (text: string) => (
          <Box
            component="span"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {text}
          </Box>
        );

        if (numSelected === total && total > 0) {
          return textWrapper('All');
        }

        return textWrapper(value.map(getOptionLabel).join(', '));
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          flexWrap: 'nowrap',
        },
      }}
      clearIcon={value.length > 0 ? undefined : null}
      componentsProps={{
        clearIndicator: {
          title: 'Clear selection (search all)',
        },
      }}
    />
  );
};
